import * as Radix from '@radix-ui/react-dropdown-menu';
import { createContext, useContext, type ReactNode, type Ref } from 'react';

import { cn } from '@/lib/cn';

import { stopPortalClick } from './portal-events';
import { usePortalTheme, type PortalThemeBinding } from './use-portal-theme';

/**
 * The menu portals to `document.body`, so it needs to be told which colour mode it belongs to —
 * exactly the problem `usePortalTheme` documents. The binding is shared from the root down to
 * the content through this context; the trigger is what anchors it, because the trigger is the
 * one part of the menu that is still inside the pane.
 */
const MenuTheme = createContext<PortalThemeBinding | null>(null);

export function DropdownMenu({
  children,
  onOpenChange,
  ...rest
}: Radix.DropdownMenuProps): ReactNode {
  const portal = usePortalTheme();
  return (
    <MenuTheme.Provider value={portal}>
      <Radix.Root
        {...rest}
        onOpenChange={(open) => {
          if (open) portal.capture();
          onOpenChange?.(open);
        }}
      >
        {children}
      </Radix.Root>
    </MenuTheme.Provider>
  );
}

export function DropdownMenuTrigger({
  ref,
  ...rest
}: Radix.DropdownMenuTriggerProps & { ref?: Ref<HTMLButtonElement> }): ReactNode {
  const portal = useContext(MenuTheme);
  return (
    <Radix.Trigger
      {...rest}
      ref={(node) => {
        portal?.anchorRef(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
    />
  );
}

export function DropdownMenuContent({
  children,
  align = 'start',
  className,
  onCloseAutoFocus,
}: {
  children: ReactNode;
  align?: 'start' | 'center' | 'end';
  className?: string;
  /**
   * Radix legger fokus tilbake på utløseren når menyen lukkes. Et valg som sender fokus et annet
   * sted — «Svar» til skrivefeltet — kaller `preventDefault()` her, ellers stjeles det tilbake.
   */
  onCloseAutoFocus?: (event: Event) => void;
}): ReactNode {
  const attributes = useContext(MenuTheme)?.attributes ?? {};
  return (
    <Radix.Portal>
      <Radix.Content
        {...attributes}
        // The menu is in document.body but still a React child of the row it was opened from.
        // See `stopPortalClick`.
        onClick={stopPortalClick}
        {...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus })}
        align={align}
        sideOffset={4}
        collisionPadding={8}
        className={cn('glass-popover z-50 min-w-56 p-1.5', className)}
      >
        {children}
      </Radix.Content>
    </Radix.Portal>
  );
}

export function DropdownMenuItem({
  children,
  onSelect,
  closeOnSelect = true,
  disabled,
  className,
  asChild,
  'data-testid': testId,
}: {
  children: ReactNode;
  onSelect?: () => void;
  /**
   * La menyen STÅ ÅPEN når raden velges — for en meny med avkryssinger.
   *
   * Radix lukker på `onSelect` med mindre hendelsen avvises, og en «Type ▾» med sju typer man
   * skal kunne krysse av flere av ville ellers lukket seg etter hver ene. Se `ListToolbar`.
   */
  closeOnSelect?: boolean;
  disabled?: boolean;
  className?: string;
  /**
   * Lar raden VÆRE barnet sitt — for en menyrad som egentlig er en lenke.
   *
   * Uten den måtte en `<Link>` ligge inne i raden, og da er det to interaktive elementer inni
   * hverandre: skjermleseren annonserer en `menuitem` som inneholder en lenke, og tastaturet har
   * to stopp for én rad. Radix' `asChild` flytter rollen og oppførselen ned på lenka i stedet, så
   * midtklikk og «åpne i ny fane» virker som på enhver annen lenke.
   */
  asChild?: boolean;
  /**
   * Menyraden kunne ikke navngis, og det oppdaget en måling 10. september.
   *
   * Hver annen kontroll i huset tar en testid; denne slukte den, så en e2e-test måtte finne raden
   * på TEKSTEN sin. Det er en test som går i stykker av en språkrettelse, og som ikke kan skille
   * to rader som heter det samme på to forskjellige menyer.
   */
  'data-testid'?: string;
}): ReactNode {
  return (
    <Radix.Item
      asChild={asChild}
      data-testid={testId}
      disabled={disabled}
      onSelect={(event) => {
        if (!closeOnSelect) event.preventDefault();
        onSelect?.();
      }}
      className={cn(
        // h-[--row-height], not h-7. A menu row is full width, so it may sit under the 44px
        // floor for the same reason a table row does — but 28px was under the 36 that argument
        // actually licenses, and this is the surface every row action goes through.
        'flex h-[var(--row-height)] cursor-pointer items-center gap-2 rounded-[var(--radius-menu-row)] px-2.5',
        'text-sm text-default outline-none',
        'data-[highlighted]:bg-[var(--fill-tonal)] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
    >
      {children}
    </Radix.Item>
  );
}

export function DropdownMenuLabel({ children }: { children: ReactNode }): ReactNode {
  return (
    <Radix.Label className="px-2.5 pt-1.5 pb-1 text-xs font-medium text-muted">
      {children}
    </Radix.Label>
  );
}

export function DropdownMenuSeparator(): ReactNode {
  return <Radix.Separator className="mx-1 my-1 h-px bg-[color:var(--line-divider)]" />;
}
